import JSZip from 'jszip'
import { type InsightsDashboardData } from '../store/insights'
import { promptFileDownload } from '../../../common/fileUtils'
import { type InsightsExportSection } from './insightsExportTypes'

const escCsv = (value: string | number | null | undefined): string => {
  let str = String(value ?? '')
  // Neutralize spreadsheet formula injection for user-derived text only — prefixing numbers would break numeric sorting in Excel.
  if (typeof value === 'string' && /^[=+\-@]/.test(str)) {
    str = `'${str}`
  }
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

const row = (...cells: Array<string | number | null | undefined>): string => cells.map(escCsv).join(',')

const trendLabel = (value: number | null): string => {
  if (value == null) {
    return 'N/A'
  }
  if (value > 0) {
    return `+${value}`
  }
  return String(value)
}

const buildDiscussionTopicsCsv = (data: InsightsDashboardData): string[] => {
  const lines: string[] = [row('Topic', 'Mentions', 'Client-Driven Meetings', 'Client-Driven %', 'Trend', 'Questions Raised')]

  for (const t of data.discussionTopics) {
    lines.push(row(t.topic, t.mentions, t.clientDrivenMeetings, `${t.clientDrivenPercent}%`, trendLabel(t.trendValue), t.questionsRaised))
  }

  return lines
}

const buildInvestmentProductsCsv = (data: InsightsDashboardData): string[] => {
  const lines: string[] = [row('Asset Class', 'Mentions', 'Client-Driven Meetings', 'Client-Driven %', 'Trend', 'Questions Raised')]

  for (const p of data.investmentProducts) {
    lines.push(row(p.subTopic, p.mentions, p.clientDrivenMeetings, `${p.clientDrivenPercent}%`, trendLabel(p.trendValue), p.questionsRaised))
  }

  return lines
}

const buildClientOutlookCsv = (data: InsightsDashboardData): string[] => {
  if (!data.clientOutlook) {
    return []
  }
  const lines: string[] = [row('Asset Class', 'Optimistic %', 'Concerned %')]

  const seen = new Set<string>()
  for (const r of [...data.clientOutlook.optimisticRows, ...data.clientOutlook.concernedRows]) {
    if (seen.has(r.assetClass)) {
      continue
    }
    seen.add(r.assetClass)
    lines.push(row(r.assetClass, `${r.optimisticPercent}%`, `${r.concernedPercent}%`))
  }

  return lines
}

const buildAssetOpportunitiesCsv = (data: InsightsDashboardData): string[] => {
  if (!data.assetOpportunities) {
    return []
  }
  const lines: string[] = [row('Meeting Type', 'Asset Mentions', 'Suggestions Made', 'Suggestions %', 'Client Agreed', 'Client Agreed %')]

  for (const r of data.assetOpportunities.rows) {
    lines.push(row(r.meetingType, r.assetMentions, r.suggestionMade, `${r.suggestionMadePct}%`, r.clientAgreed, `${r.clientAgreedPct}%`))
  }

  return lines
}

const buildReferralsCsv = (data: InsightsDashboardData): string[] => {
  if (!data.referrals) {
    return []
  }
  const lines: string[] = [row('Meeting Type', 'Meetings', 'Referrals Asked', 'Referrals Provided')]

  for (const r of data.referrals.rows) {
    lines.push(row(r.meetingType, r.meetings, r.referralsAsked, r.referralsProvided))
  }

  return lines
}

const SECTION_BUILDERS: Record<InsightsExportSection, (data: InsightsDashboardData) => string[]> = {
  discussionTopics: buildDiscussionTopicsCsv,
  investmentProducts: buildInvestmentProductsCsv,
  clientOutlook: buildClientOutlookCsv,
  assetOpportunities: buildAssetOpportunitiesCsv,
  referrals: buildReferralsCsv,
}

const SECTION_FILE_NAMES: Record<InsightsExportSection, string> = {
  discussionTopics: 'discussion_topics',
  investmentProducts: 'investment_products',
  clientOutlook: 'client_outlook',
  assetOpportunities: 'asset_opportunities',
  referrals: 'referrals',
}

const formatDate = (): string => {
  const now = new Date()
  const dd = String(now.getDate()).padStart(2, '0')
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const yyyy = now.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

const UTF8_BOM = '\uFEFF'
const CSV_LINE_ENDING = '\r\n'

const renderCsv = (lines: string[]): string => `${UTF8_BOM}${lines.join(CSV_LINE_ENDING)}`

export const exportInsightsCsv = async (data: InsightsDashboardData, sections: InsightsExportSection[]) => {
  const date = formatDate()

  if (sections.length === 1) {
    const lines = SECTION_BUILDERS[sections[0]](data)
    if (lines.length === 0) {
      return
    }
    const blob = new Blob([renderCsv(lines)], { type: 'text/csv;charset=utf-8;' })
    await promptFileDownload(blob, `zocks_insights_${SECTION_FILE_NAMES[sections[0]]}_${date}.csv`)
    return
  }

  const zip = new JSZip()
  for (const section of sections) {
    const lines = SECTION_BUILDERS[section](data)
    if (lines.length > 0) {
      zip.file(`zocks_insights_${SECTION_FILE_NAMES[section]}_${date}.csv`, renderCsv(lines))
    }
  }

  const blob = await zip.generateAsync({ type: 'blob' })
  await promptFileDownload(blob, `zocks_insights_export_${date}.zip`)
}
